import { RevolutionAnimation } from '@theme/revolution-animation';

const SNAPSHOT_PREFIX = 'revolution:load-more-state:';

/**
 * <load-more-paginator>
 *
 * Click-based pagination for paginated grids.
 * Fetches the next paginated page via section_id rendering, appends new
 * cards to the grid, updates the loaded/total counter, and the URL.
 *
 * Required dataset:
 *   data-grid-selector   — CSS selector for the grid container to append into
 *   data-section-id      — Shopify section id for section_id render
 *   data-next-url        — full URL of the next page (?page=N)
 *   data-loaded          — number of items currently visible
 *   data-total           — total items in the paginated source
 *
 * Optional dataset:
 *   data-item-selector   — CSS selector for appendable items inside the grid
 *   data-page-size       — page size used when rebuilding a missing next URL
 *   data-restore-pages-from-url — rebuild pages 1..N when URL has ?page=N
 *
 * Markup contract (children, scoped to this element):
 *   button[type="button"]     the clickable button (.LoadMore--Button or .collection-grid__load-more-btn)
 *   .LoadMore--ButtonText      text node swapped between "Load more" / "Loading..."
 *   .LoadMore--Counter         text node "{loaded}/{total}"
 */

class LoadMorePaginator extends HTMLElement {
  #button = null;
  #buttonTexts = [];
  #counter = null;
  #grid = null;
  #loading = false;
  #abortController = null;
  #labelLoadMore = 'Load more';
  #labelLoading = 'Loading...';
  #boundOnClick = null;
  #itemSelector = '.collection-grid__product-cell';
  #restoringFromUrl = false;

  connectedCallback() {
    this.#button =
      this.querySelector('.LoadMore--Button, .collection-grid__load-more-btn') ??
      this.querySelector('button[type="button"]');
    this.#buttonTexts = Array.from(this.querySelectorAll('.LoadMore--ButtonText'));
    this.#counter = this.querySelector('.LoadMore--Counter');
    this.#grid = document.querySelector(this.dataset.gridSelector);

    this.#labelLoadMore = this.dataset.labelLoadMore || 'Load more';
    this.#labelLoading = this.dataset.labelLoading || 'Loading...';
    this.#itemSelector = this.dataset.itemSelector || '.collection-grid__product-cell';

    if (!this.#button || !this.#grid) {
      return;
    }

    if (this.#boundOnClick) {
      return;
    }

    this.#boundOnClick = (event) => this.#onClick(event);
    this.#button.addEventListener('click', this.#boundOnClick);

    if (!this.dataset.nextUrl) {
      const fallbackUrl = this.#buildNextPageUrl();
      if (fallbackUrl) {
        this.dataset.nextUrl = fallbackUrl;
        this.removeAttribute('data-exhausted');
        this.#button.removeAttribute('disabled');
      }
    }

    if (window.__collectionSnapshotRestored) {
      this.#syncGridCounts();
    }

    this.#restorePagesFromUrl();
  }

  disconnectedCallback() {
    if (this.#boundOnClick) this.#button?.removeEventListener('click', this.#boundOnClick);
    this.#abortController?.abort();
  }

  async #onClick(event) {
    event.preventDefault();
    if (this.#loading || this.#restoringFromUrl) return;

    const nextUrl = this.dataset.nextUrl || this.#buildNextPageUrl();
    const sectionId = this.dataset.sectionId;
    if (!nextUrl || !sectionId) return;

    if (nextUrl !== this.dataset.nextUrl) {
      this.dataset.nextUrl = nextUrl;
    }

    this.#setLoading(true);

    try {
      this.#abortController = new AbortController();

      const fetchUrl = new URL(nextUrl, window.location.origin);
      fetchUrl.searchParams.set('section_id', sectionId);

      const response = await fetch(fetchUrl.toString(), { signal: this.#abortController.signal });
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const newGrid = doc.querySelector(this.dataset.gridSelector);
      const newPaginator = doc.querySelector('load-more-paginator');
      let appendedCount = 0;

      if (newGrid) {
        const appendedNodes = this.#getGridItems(newGrid);
        if (appendedNodes.length) {
          const fragment = document.createDocumentFragment();
          appendedNodes.forEach((node) => fragment.appendChild(node));
          this.#grid.appendChild(fragment);
          this.#revealAppendedNodes(appendedNodes);
          appendedCount = appendedNodes.length;
        }
      }

      const shownBeforeUpdate = this.#getVisibleProductCount();
      const totalCount = parseInt(this.dataset.total ?? '0', 10);

      if (appendedCount === 0 && shownBeforeUpdate < totalCount) {
        console.error('[load-more-paginator] Next page returned no items.');
        return;
      }

      this.#updateHistoryUrl(nextUrl);
      this.#updateCounter(newPaginator);
      this.#syncGridCounts();
      this.#saveSnapshot();

      this.dispatchEvent(
        new CustomEvent('load-more:appended', {
          bubbles: true,
          detail: {
            loaded: this.dataset.loaded,
            total: this.dataset.total,
          },
        })
      );
    } catch (error) {
      if (error.name === 'AbortError') {
        return;
      }
      console.error('[load-more-paginator]', error);
    } finally {
      this.#setLoading(false);
    }
  }

  #setLoading(isLoading) {
    this.#loading = isLoading;
    const label = isLoading ? this.#labelLoading : this.#labelLoadMore;
    this.#buttonTexts.forEach((node) => {
      node.textContent = label;
    });
    if (!this.#button) {
      return;
    }
    if (isLoading) {
      this.setAttribute('data-loading', 'true');
      this.#button.setAttribute('aria-busy', 'true');
      this.#button.setAttribute('disabled', '');
    } else {
      this.removeAttribute('data-loading');
      this.#button.removeAttribute('aria-busy');
      if (!this.hasAttribute('data-exhausted')) {
        this.#button.removeAttribute('disabled');
      }
    }
  }

  #updateCounter(newPaginator) {
    if (newPaginator) {
      this.dataset.total = newPaginator.dataset.total || this.dataset.total;
      this.dataset.nextUrl = newPaginator.dataset.nextUrl || '';
    } else {
      this.dataset.nextUrl = '';
    }

    if (!this.dataset.nextUrl) {
      const fallbackUrl = this.#buildNextPageUrl();
      if (fallbackUrl) {
        this.dataset.nextUrl = fallbackUrl;
      }
    }

    this.#syncGridCounts();

    const shown = this.#getVisibleProductCount();
    const totalCount = parseInt(this.dataset.total ?? '0', 10);

    if (shown >= totalCount || !this.dataset.nextUrl) {
      this.#disableExhausted();
      return;
    }

    this.removeAttribute('data-exhausted');
    this.#button?.removeAttribute('disabled');
  }

  async #restorePagesFromUrl() {
    if (
      this.dataset.restorePagesFromUrl !== 'true' ||
      this.#restoringFromUrl ||
      window.__collectionSnapshotRestored
    ) {
      return;
    }

    const targetPage = parseInt(new URL(window.location.href).searchParams.get('page') ?? '1', 10);
    if (!Number.isFinite(targetPage) || targetPage <= 1) {
      return;
    }

    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      return;
    }

    this.#restoringFromUrl = true;
    this.#setLoading(true);
    this.setAttribute('data-restoring', 'true');

    try {
      if (this.#restoreSnapshot(targetPage)) {
        return;
      }

      this.#abortController = new AbortController();
      const documents = [];

      for (let page = 1; page <= targetPage; page += 1) {
        documents.push(await this.#fetchPageDocument(page, sectionId));
      }

      const firstGrid = documents[0]?.querySelector(this.dataset.gridSelector);
      if (!firstGrid) {
        return;
      }

      this.#grid.replaceChildren(...this.#getGridItems(firstGrid));

      documents.slice(1).forEach((doc) => {
        const nextGrid = doc.querySelector(this.dataset.gridSelector);
        if (!nextGrid) {
          return;
        }

        const fragment = document.createDocumentFragment();
        this.#getGridItems(nextGrid).forEach((node) => fragment.appendChild(node));
        this.#grid.appendChild(fragment);
      });

      this.#rerunAnimationsAfterRestore();

      const latestPaginator = documents[documents.length - 1]?.querySelector('load-more-paginator');
      this.#updateCounter(latestPaginator);
      this.#syncGridCounts();
      this.#saveSnapshot();

      this.dispatchEvent(
        new CustomEvent('load-more:appended', {
          bubbles: true,
          detail: {
            loaded: this.dataset.loaded,
            total: this.dataset.total,
          },
        })
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('[load-more-paginator]', error);
      }
    } finally {
      this.#restoringFromUrl = false;
      this.removeAttribute('data-restoring');
      this.#setLoading(false);
      if (!this.dataset.nextUrl) {
        this.#disableExhausted();
      }
    }
  }

  async #fetchPageDocument(page, sectionId) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.searchParams.set('section_id', sectionId);

    const response = await fetch(url.toString(), { signal: this.#abortController.signal });
    if (!response.ok) {
      throw new Error(`Fetch failed: ${response.status}`);
    }

    const html = await response.text();
    return new DOMParser().parseFromString(html, 'text/html');
  }

  #getSnapshotKey() {
    const url = new URL(window.location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');

    const normalizedSearch = url.searchParams.toString();
    return `${SNAPSHOT_PREFIX}${url.pathname}${normalizedSearch ? `?${normalizedSearch}` : ''}`;
  }

  #readSnapshot() {
    try {
      const raw = sessionStorage.getItem(this.#getSnapshotKey());
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  #getSnapshotGridHtml() {
    const clone = this.#grid.cloneNode(true);
    clone.querySelectorAll('.animatedContent[data-loaded]').forEach((node) => {
      node.removeAttribute('data-loaded');
    });

    return clone.innerHTML;
  }

  #saveSnapshot() {
    if (!this.#grid || this.dataset.restorePagesFromUrl !== 'true') {
      return;
    }

    const payload = {
      gridHtml: this.#getSnapshotGridHtml(),
      gridSelector: this.dataset.gridSelector ?? '',
      itemSelector: this.#itemSelector,
      sectionId: this.dataset.sectionId ?? '',
      loaded: this.dataset.loaded ?? String(this.#getVisibleProductCount()),
      total: this.dataset.total ?? '',
      nextUrl: this.dataset.nextUrl ?? '',
      exhausted: this.hasAttribute('data-exhausted'),
      currentPage: this.#getCurrentPage(),
      pageSize: this.#getPageSize(),
      savedAt: Date.now(),
    };

    try {
      sessionStorage.setItem(this.#getSnapshotKey(), JSON.stringify(payload));
    } catch {
      /* Storage may be disabled or full; URL fallback fetches still handle restore. */
    }
  }

  #restoreSnapshot(targetPage) {
    const snapshot = this.#readSnapshot();
    if (!this.#isSnapshotValid(snapshot, targetPage)) {
      return false;
    }

    this.#grid.innerHTML = snapshot.gridHtml;
    this.dataset.loaded = snapshot.loaded ?? String(this.#getVisibleProductCount());
    this.dataset.total = snapshot.total ?? this.dataset.total ?? '';
    this.dataset.nextUrl = snapshot.nextUrl ?? '';

    if (snapshot.exhausted || !this.dataset.nextUrl) {
      this.#disableExhausted();
    } else {
      this.removeAttribute('data-exhausted');
      this.#button?.removeAttribute('disabled');
    }

    this.#rerunAnimationsAfterRestore();
    this.#syncGridCounts();

    this.dispatchEvent(
      new CustomEvent('load-more:appended', {
        bubbles: true,
        detail: {
          loaded: this.dataset.loaded,
          total: this.dataset.total,
        },
      })
    );

    return true;
  }

  #isSnapshotValid(snapshot, targetPage) {
    if (!snapshot?.gridHtml) {
      return false;
    }

    if (snapshot.gridSelector && snapshot.gridSelector !== this.dataset.gridSelector) {
      return false;
    }

    if (snapshot.itemSelector && snapshot.itemSelector !== this.#itemSelector) {
      return false;
    }

    if (snapshot.sectionId && snapshot.sectionId !== this.dataset.sectionId) {
      return false;
    }

    const pageSize = this.#getPageSize();
    if (snapshot.pageSize && pageSize && Number(snapshot.pageSize) !== pageSize) {
      return false;
    }

    return Number(snapshot.currentPage) === targetPage;
  }

  #getPageSize() {
    return parseInt(
      this.dataset.pageSize ?? document.getElementById('mainGrid')?.dataset.productsPerPage ?? '0',
      10
    );
  }

  #getCurrentPage() {
    const shown = this.#getVisibleProductCount();
    const perPage = this.#getPageSize();

    if (perPage > 0 && shown > 0) {
      return Math.max(1, Math.ceil(shown / perPage));
    }

    return parseInt(new URL(window.location.href).searchParams.get('page') ?? '1', 10) || 1;
  }

  #buildNextPageUrl() {
    const shown = this.#getVisibleProductCount();
    const total = parseInt(this.dataset.total ?? '0', 10);

    if (!Number.isFinite(total) || shown >= total) {
      return '';
    }

    const perPage = this.#getPageSize();
    const currentPage = perPage > 0 ? Math.max(1, Math.ceil(shown / perPage)) : 1;
    const nextPage = currentPage + 1;

    if (this.dataset.pageMode === 'search') {
      const fetchUrl = new URL(window.location.pathname, window.location.origin);
      const currentParams = new URLSearchParams(window.location.search);

      currentParams.forEach((value, key) => {
        fetchUrl.searchParams.set(key, value);
      });

      if (!fetchUrl.searchParams.get('q')) {
        return '';
      }

      fetchUrl.searchParams.set('type', 'product');
      fetchUrl.searchParams.set('options[prefix]', 'last');
      fetchUrl.searchParams.set('page', String(nextPage));
      fetchUrl.searchParams.delete('section_id');

      return fetchUrl.pathname + fetchUrl.search;
    }

    const fetchUrl = new URL(window.location.href);
    fetchUrl.searchParams.set('page', String(nextPage));
    fetchUrl.searchParams.delete('section_id');

    return fetchUrl.pathname + fetchUrl.search;
  }

  #updateHistoryUrl(loadedUrl) {
    if (!loadedUrl || !window.history?.replaceState) {
      return;
    }

    const url = new URL(loadedUrl, window.location.origin);
    url.searchParams.delete('section_id');

    const page = parseInt(url.searchParams.get('page') ?? '1', 10);
    if (!Number.isFinite(page) || page <= 1) {
      url.searchParams.delete('page');
    }

    history.replaceState(
      {
        searchParams: url.search.substring(1),
        pathname: url.pathname,
      },
      '',
      url.toString()
    );
  }

  #getVisibleProductCount() {
    if (!this.#grid) {
      return 0;
    }

    return this.#getGridItems(this.#grid).length;
  }

  #getGridItems(grid) {
    const items = Array.from(grid.querySelectorAll(this.#itemSelector));
    return items.length ? items : Array.from(grid.children);
  }

  #syncGridCounts() {
    const mainGrid = document.getElementById('mainGrid');
    const shown = this.#getVisibleProductCount();
    const total = parseInt(this.dataset.total ?? mainGrid?.dataset.productCount ?? '0', 10);
    const shownText = String(shown);
    const totalText = Number.isFinite(total)
      ? String(total)
      : (mainGrid?.dataset.productCount ?? '');

    this.dataset.loaded = shownText;
    if (mainGrid) {
      mainGrid.dataset.productsShown = shownText;
    }

    if (totalText !== '') {
      if (mainGrid) {
        mainGrid.dataset.productCount = totalText;
      }
      this.dataset.total = totalText;
    }

    if (this.#counter && totalText !== '') {
      this.#counter.textContent = `${shownText}/${totalText}`;
    }

    this.#updateToolbarCounts(shownText, totalText);

    document.dispatchEvent(
      new CustomEvent('collection-grid:counts-updated', {
        bubbles: true,
        detail: {
          loaded: shown,
          total: totalText !== '' ? parseInt(totalText, 10) : undefined,
        },
      })
    );
  }

  #updateToolbarCounts(shown, total) {
    const shownEl = document.querySelector('[data-toolbar-products-shown]');
    const totalEl = document.querySelector('[data-toolbar-products-total]');

    if (shownEl) {
      shownEl.textContent = shown;
    }

    if (totalEl && total !== '') {
      totalEl.textContent = total;
    }
  }

  #revealAppendedNodes(nodes) {
    if (!window.theme?.enableAnimations) {
      return;
    }

    const cells = nodes.filter((node) => node.classList?.contains('animatedContent'));
    if (!cells.length) {
      return;
    }

    const anim = new RevolutionAnimation(null, true);
    anim.elementFadeIn(cells);
  }

  #rerunAnimationsAfterRestore() {
    if (!window.theme?.enableAnimations) {
      return;
    }

    const cells = this.#getGridItems(this.#grid).filter((node) =>
      node.classList?.contains('animatedContent')
    );
    if (!cells.length) {
      return;
    }

    cells.forEach((node) => node.removeAttribute('data-loaded'));

    const anim = new RevolutionAnimation(this, true);
    anim.elementFadeIn(cells);
  }

  #disableExhausted() {
    this.setAttribute('data-exhausted', 'true');
    this.#button?.setAttribute('disabled', '');
  }
}

if (!customElements.get('load-more-paginator')) {
  customElements.define('load-more-paginator', LoadMorePaginator);
}
